import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NotificationAttachment } from 'src/app/shared/models/notification-attachment.model';
import { NotificationAttachmentService } from 'src/app/shared/services/notification-attachment.service';
import {
  formatFileSize,
  getFileIconClass,
  getFileTypeLabel,
  getPreviewKind
} from 'src/app/shared/utils/file-type.util';

@Component({
  selector: 'app-notification-attachment-card',
  templateUrl: './notification-attachment-card.component.html',
  styleUrls: ['./notification-attachment-card.component.scss']
})
export class NotificationAttachmentCardComponent implements OnInit, OnDestroy {
  @Input() attachment!: NotificationAttachment;
  @Output() preview = new EventEmitter<NotificationAttachment>();
  @Output() download = new EventEmitter<NotificationAttachment>();

  fileIconClass = 'icon-file';
  fileTypeLabel = 'File';
  sizeLabel = '';
  safeThumbnailUrl: SafeResourceUrl | null = null;
  thumbnailFailed = false;
  isImage = false;

  private blobUrl = '';

  constructor(
    private attachmentService: NotificationAttachmentService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    if (!this.attachment) { return; }
    this.fileIconClass = getFileIconClass(this.attachment.fileName, this.attachment.mimeType);
    this.fileTypeLabel = getFileTypeLabel(this.attachment.fileName, this.attachment.mimeType);
    this.sizeLabel = formatFileSize(this.attachment.fileSize);
    this.isImage = getPreviewKind(this.attachment.fileName, this.attachment.mimeType) === 'image';
    if (this.isImage) {
      this.loadThumbnail();
    }
  }

  ngOnDestroy(): void {
    if (this.blobUrl) {
      this.attachmentService.revokeObjectUrl(this.blobUrl);
    }
  }

  onPreview(event: Event): void {
    event.stopPropagation();
    this.preview.emit(this.attachment);
  }

  onDownload(event: Event): void {
    event.stopPropagation();
    this.download.emit(this.attachment);
  }

  onThumbnailError(): void {
    this.thumbnailFailed = true;
    if (this.blobUrl) {
      this.attachmentService.revokeObjectUrl(this.blobUrl);
      this.blobUrl = '';
    }
    this.safeThumbnailUrl = null;
  }

  private loadThumbnail(): void {
    this.attachmentService.fetchBlob(this.attachment).subscribe({
      next: (blob) => {
        const imageBlob = blob.type.startsWith('image/')
          ? blob
          : new Blob([blob], { type: this.attachment.mimeType || 'image/jpeg' });

        this.blobUrl = this.attachmentService.createObjectUrl(imageBlob);
        this.safeThumbnailUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.blobUrl);
      },
      error: () => { /* keep icon fallback */ }
    });
  }
}
